import { Head, Link } from "@inertiajs/react"
import { Suspense, lazy, useEffect, useMemo, useState } from "react"
import { Printer } from "lucide-react"

import { AppShell } from "@/components/AppShell"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Select } from "@/components/ui/select"
import { SegmentedControl } from "@/components/ui/segmented-control"
import { Spinner } from "@/components/ui/progress"
import { computeAddOns, roomFigures, surfaceFigures } from "@/editor-core/addons"
import { computeAllRooms, rollAssignments } from "@/components/editor/quantities"
import {
  type CutPlans,
  type Subfloor,
  type WasteMode,
  type WireAddOn,
  type WireProductAddOn,
  type WireRoom,
  type WireSurface,
  addOnsFromServer,
  calibrationsFromSheets,
  roomsFromServer,
  rulesFromServer,
  surfacesFromServer,
} from "@/editor-core/document"
import { EM_DASH } from "@/editor-core/labels"
import { type PlacedImage } from "@/editor-core/schedule"
import { toSeamSettings } from "@/editor-core/seams"
import { worksheetLines } from "@/editor-core/worksheet"
import { type HeaderPair, HeaderColumn } from "@/components/print/PrintSheetHeader"
// plan-layout is the pure geometry module — safe for this SSR'd page to
// import by VALUE (plan-export pulls Konva and stays behind the lazy figure).
import {
  PAPER_CSS,
  type Paper,
  type PaperOrientation,
  type PaperSize,
} from "@/components/editor/plan-layout"
import type { PlanExportState } from "@/components/editor/PlanFigure"
import type { EditorSheet } from "@/types/takeoff"
import type { AdminSettings } from "@/types/inertia"

// The marked-up plan — the drawing itself, printed: the sheet with pattern
// fills, room names, and seam diagrams exactly as drawn, a legend decoding
// product color → rooms → quantities, and a Floor Authority title block. The
// deliverable a builder or installer reads cold.
//
// Everything derives in one useMemo chain from the same editor-core modules
// the editor renders from — the legend runs the worksheet's own derivation,
// and the figure re-renders the canvas through the editor's own components —
// so the printed sheet and the editor can never disagree. The figure is
// Konva, so it lazy-loads behind a mount gate and never enters SSR.

const PlanFigure = lazy(() => import("@/components/editor/PlanFigure"))

type Props = {
  takeoff: {
    id: number
    name: string
    url: string
    owner: { kind: "plan" | "project"; id: number; display_name: string; url: string }
  }
  header: { title: string; subtitle: string | null; left: HeaderPair[]; right: HeaderPair[] }
  document: {
    revision: number
    rooms: WireRoom[]
    surfaces: WireSurface[]
    add_ons: WireAddOn[]
    cut_plans: CutPlans
  }
  product_add_ons: WireProductAddOn[]
  settings: AdminSettings
  sheets: EditorSheet[]
  active_sheet_id: number | null
  sheet_images: Record<number, PlacedImage[]>
  project_subfloor: Subfloor
  waste_mode_default: WasteMode
}

const fmt = (n: number | null): string =>
  n == null ? EM_DASH : Number.isInteger(n) ? String(n) : n.toFixed(1)

export default function MarkedUpPlanPage(props: Props) {
  const { takeoff, header } = props
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  // Stamped after mount: a render-time date would SSR-mismatch, and the only
  // date that matters is the one on the paper coming out of the printer.
  const [printDate, setPrintDate] = useState<string | null>(null)
  useEffect(() => setPrintDate(new Date().toLocaleDateString()), [])

  // ?dpi= (96–300, default 300): a debug/test valve — system tests export at
  // 96 so the suite doesn't spend its time PNG-encoding print-grade pixels.
  const [dpi] = useState<number>(() => {
    if (typeof window === "undefined") return 300
    const raw = Number(new URLSearchParams(window.location.search).get("dpi"))
    return Number.isFinite(raw) && raw > 0 ? Math.min(Math.max(raw, 96), 300) : 300
  })

  const [showLabels, setShowLabels] = useState(true)
  const [showQuantities, setShowQuantities] = useState(true)
  const [paperSize, setPaperSize] = useState<PaperSize>("letter")
  const [orientation, setOrientation] = useState<PaperOrientation>("portrait")
  const paper = useMemo<Paper>(
    () => ({ size: paperSize, orientation }),
    [paperSize, orientation]
  )
  // What the DISPLAYED figure was exported for: the @page rule and the Print
  // button follow this, so a print can never pair the old figure with the
  // new paper (or catch a half-finished re-export).
  const [exportState, setExportState] = useState<PlanExportState>({
    exporting: true,
    exportedPaper: null,
  })
  const printPaper = exportState.exportedPaper ?? paper

  // The full worksheet derivation, once, takeoff-wide — the legend is a
  // per-sheet slice of the exact numbers the worksheet prints.
  const derived = useMemo(() => {
    const rooms = roomsFromServer(props.document.rooms)
    const surfaces = surfacesFromServer(props.document.surfaces)
    const seamSettings = toSeamSettings(props.settings)
    const calibBySheet = calibrationsFromSheets(props.sheets)
    const allComputed = computeAllRooms(rooms, calibBySheet, seamSettings, props.waste_mode_default)
    const assignments = rollAssignments(rooms, calibBySheet, props.document.cut_plans, seamSettings)
    const computedAddOns = computeAddOns({
      addOns: addOnsFromServer(props.document.add_ons),
      rules: rulesFromServer(props.product_add_ons),
      rooms: rooms.map((room) =>
        roomFigures(
          room,
          calibBySheet[room.sheetId]?.ftPerPx ?? null,
          props.sheet_images[room.sheetId] ?? [],
          props.project_subfloor
        )
      ),
      surfaces: surfaces.map((surface) => surfaceFigures(surface, props.project_subfloor)),
    })
    const { lines } = worksheetLines({
      rooms: allComputed,
      surfaces,
      addOns: computedAddOns,
      assignments,
    })
    return { allComputed, seamSettings, lines }
  }, [props])

  // Only sheets with something drawn on them are printable.
  const printableSheets = useMemo(
    () => props.sheets.filter((sheet) => derived.allComputed.some((r) => r.sheetId === sheet.id)),
    [props.sheets, derived.allComputed]
  )
  const [sheetId, setSheetId] = useState<number | null>(() => {
    const requested = props.active_sheet_id
    if (requested && printableSheets.some((s) => s.id === requested)) return requested
    return printableSheets[0]?.id ?? null
  })
  const activeSheet = printableSheets.find((s) => s.id === sheetId) ?? printableSheets[0] ?? null
  // Identity-stable for PlanFigure's input memo: a fresh literal per render
  // plus the onExportState re-render made the export restart itself forever
  // (the flashing-figure bug, found by RT on 914 North Dogwood).
  const sheetSize = useMemo(
    () =>
      activeSheet && activeSheet.width != null && activeSheet.height != null
        ? { width: activeSheet.width, height: activeSheet.height }
        : null,
    [activeSheet]
  )

  // The per-sheet slice: this sheet's rooms, the legend lines that touch
  // them, and the qty sublabels the figure stamps under room names. One
  // number source — a legend row and its on-plan sublabel can't drift.
  const sheetView = useMemo(() => {
    if (!activeSheet) return null
    const sheetRooms = derived.allComputed.filter((r) => r.sheetId === activeSheet.id)
    const idSet = new Set(sheetRooms.map((r) => r.id))
    const legend = derived.lines
      .filter((line) => line.kind === "product")
      .map((line) => ({ ...line, rooms: line.rooms.filter((d) => idSet.has(d.rowId)) }))
      .filter((line) => line.rooms.length > 0)
    const sublabels: Record<string, string> = {}
    for (const line of legend) {
      for (const detail of line.rooms) {
        if (detail.netQty != null) {
          // A multiplier room is drawn once and counted N times: stamp the
          // DRAWN copy's area plus the ×N, not the multiplied total — a
          // "120 SF" inside a visibly 10 SF polygon misreads on paper. The
          // legend keeps the multiplied figure.
          const unit = detail.unit ?? line.unit
          sublabels[detail.rowId] =
            detail.multiplier > 1
              ? `${fmt(detail.netQty / detail.multiplier)} ${unit} ×${detail.multiplier}`
              : `${fmt(detail.netQty)} ${unit}`
        }
      }
    }
    const unassigned = sheetRooms.filter((r) => !r.product).map((r) => r.name)
    return { sheetRooms, legend, sublabels, unassigned }
  }, [activeSheet, derived])

  const title = `Marked-up Plan — ${header.title}`
  const description =
    "The measured plan as drawn — pattern fills, room names, seam diagrams, and a product legend with quantities."

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      {/* Per-document paper: later in the cascade than the global letter
          portrait rule, so the other printable documents keep theirs. Driven
          by the EXPORTED paper — the displayed figure and the sheet size
          change together, never out of step. */}
      <style>{`@page { size: ${PAPER_CSS[printPaper.size][printPaper.orientation]}; margin: 0.5in; }`}</style>
      <AppShell>
        <div className="print-chrome mb-4 flex flex-wrap items-center gap-3">
          <Link href={takeoff.url} className="no-underline hover:underline">
            ← {takeoff.name}
          </Link>
          {printableSheets.length > 1 && (
            <Select
              aria-label="Sheet"
              value={activeSheet?.id ?? ""}
              onChange={(e) => {
                const id = Number(e.target.value)
                setSheetId(id)
                // Same shareable-URL convention as the editor's sheet tabs.
                window.history.replaceState(null, "", `${window.location.pathname}?sheet=${id}`)
              }}
              className="w-auto"
              data-testid="marked-up-plan-sheet"
            >
              {printableSheets.map((sheet) => (
                <option key={sheet.id} value={sheet.id}>
                  {sheet.label}
                </option>
              ))}
            </Select>
          )}
          <label className="flex items-center gap-2 font-normal text-ink-body">
            <Checkbox
              checked={showLabels}
              onChange={(e) => setShowLabels(e.target.checked)}
              data-testid="toggle-labels"
            />
            Room labels
          </label>
          <label className="flex items-center gap-2 font-normal text-ink-body">
            <Checkbox
              checked={showQuantities}
              onChange={(e) => setShowQuantities(e.target.checked)}
              data-testid="toggle-quantities"
            />
            Quantities
          </label>
          <span className="ml-auto flex flex-wrap items-center gap-2">
            <SegmentedControl
              options={[
                { value: "letter", label: "Letter" },
                { value: "tabloid", label: "Tabloid" },
              ]}
              value={paperSize}
              onChange={(value) => setPaperSize(value as PaperSize)}
            />
            <SegmentedControl
              options={[
                { value: "portrait", label: "Portrait" },
                { value: "landscape", label: "Landscape" },
              ]}
              value={orientation}
              onChange={(value) => setOrientation(value as PaperOrientation)}
            />
            <Button
              variant="soft"
              onClick={() => window.print()}
              disabled={exportState.exporting}
              data-testid="print-marked-up-plan"
            >
              <Printer className="mr-1.5 h-4 w-4" />
              {exportState.exporting ? "Rendering…" : "Print / Save as PDF"}
            </Button>
          </span>
        </div>

        <article className="print-sheet mx-auto max-w-5xl rounded-lg border border-hairline bg-surface p-6">
          <header className="flex flex-wrap items-center justify-between gap-4 border-b border-hairline pb-3">
            <div>
              <h1>Marked-up Plan</h1>
              <p className="text-ink-display">
                {header.title}
                {header.subtitle && <span className="text-ink-muted"> · {header.subtitle}</span>}
              </p>
            </div>
            <img
              src="/floor-authority-logo-navy.svg"
              alt="Floor Authority"
              className="print-logo h-9 w-auto dark:hidden"
            />
            <img
              src="/floor-authority-logo-white.svg"
              alt=""
              aria-hidden="true"
              className="print-chrome hidden h-9 w-auto dark:block"
            />
          </header>

          <div className="mt-4 grid gap-x-8 gap-y-1 sm:grid-cols-2">
            <HeaderColumn
              pairs={[...header.left, ["Sheet", activeSheet?.label ?? null] as HeaderPair]}
            />
            <HeaderColumn pairs={[...header.right, ["Date", printDate] as HeaderPair]} />
          </div>

          {sheetView && sheetView.unassigned.length > 0 && (
            <div className="callout print-chrome mt-4" data-testid="marked-up-plan-unassigned">
              No product yet: {sheetView.unassigned.join(", ")} — these rooms print without a
              legend entry.
            </div>
          )}

          <div className="mt-5 break-inside-avoid">
            {!activeSheet || !sheetView ? (
              <div className="callout" data-testid="marked-up-plan-empty">
                Nothing drawn yet — trace rooms in the editor and they'll print here.
              </div>
            ) : mounted ? (
              <Suspense
                fallback={
                  <div className="flex min-h-48 items-center justify-center">
                    <Spinner />
                  </div>
                }
              >
                <PlanFigure
                  key={activeSheet.id}
                  rooms={sheetView.sheetRooms}
                  images={activeSheet.images}
                  sheetSize={sheetSize}
                  sheetKind={activeSheet.kind}
                  mirrored={activeSheet.mirrored}
                  ftPerPx={activeSheet.ft_per_px}
                  settings={derived.seamSettings}
                  showLabels={showLabels}
                  sublabels={showQuantities ? sheetView.sublabels : undefined}
                  paper={paper}
                  dpi={dpi}
                  onExportState={setExportState}
                />
              </Suspense>
            ) : (
              <div className="flex min-h-48 items-center justify-center">
                <Spinner />
              </div>
            )}
          </div>

          {sheetView && (
            <section className="mt-6">
              <h2>Legend</h2>
              {sheetView.legend.length === 0 ? (
                <p className="mt-2 text-ink-muted">
                  No products assigned on this sheet yet — assign them in the editor.
                </p>
              ) : (
                <div className="mt-2 overflow-x-auto">
                  <table className="w-full border-collapse text-sm" data-testid="marked-up-plan-legend">
                    <thead>
                      <tr className="border-b border-ink-muted text-left align-bottom text-xs uppercase tracking-wide text-ink-muted">
                        <th className="px-1.5 pb-1 font-semibold">Product</th>
                        <th className="px-1.5 pb-1 font-semibold">Rooms (net)</th>
                        <th className="px-1.5 pb-1 text-right font-semibold">Net</th>
                        <th className="px-1.5 pb-1 text-right font-semibold">Order</th>
                        <th className="px-1.5 pb-1 text-right font-semibold">Unit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sheetView.legend.map((line) => {
                        const net = sum(line.rooms.map((d) => (d.unit ? null : d.netQty)))
                        const order = sum(line.rooms.map((d) => (d.unit ? null : d.orderQty)))
                        return (
                          <tr
                            key={line.key}
                            className="schedule-row border-b border-hairline align-top"
                            data-testid={`legend-line-${line.key}`}
                          >
                            <td className="px-1.5 py-2">
                              <span className="flex items-center gap-1.5">
                                {line.color && (
                                  <span
                                    className="h-3 w-3 shrink-0 rounded-sm"
                                    style={{ background: line.color }}
                                    aria-hidden="true"
                                  />
                                )}
                                <span className="font-medium">{line.label}</span>
                              </span>
                            </td>
                            <td className="px-1.5 py-2 text-ink-body">
                              {line.rooms
                                .map(
                                  (d) =>
                                    `${d.label}${d.multiplier > 1 ? ` ×${d.multiplier}` : ""} (${fmt(d.netQty)}${d.unit ? ` ${d.unit}` : ""})`
                                )
                                .join(", ")}
                            </td>
                            <td className="px-1.5 py-2 text-right tabular-nums">{fmt(net)}</td>
                            <td className="px-1.5 py-2 text-right font-semibold tabular-nums">
                              {fmt(order)}
                            </td>
                            <td className="px-1.5 py-2 text-right">{line.unit}</td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="mt-2 text-xs text-ink-muted">
                Order figures are this sheet's rooms only — purchasing totals live on the order
                worksheet.
              </p>
            </section>
          )}

          <footer className="mt-6 border-t border-hairline pt-2 text-xs text-ink-muted">
            {header.title}
            {header.subtitle && ` · ${header.subtitle}`} · {takeoff.name}
            {activeSheet && ` · ${activeSheet.label}`}
            {printDate && ` · Printed ${printDate}`}
          </footer>
        </article>
      </AppShell>
    </>
  )
}

/** Sum ignoring nulls; null when nothing summed (unit-overridden details —
 *  an EA figure under an SF line — never fold in). */
function sum(values: (number | null)[]): number | null {
  let total: number | null = null
  for (const v of values) {
    if (v == null) continue
    total = (total ?? 0) + v
  }
  return total
}
